import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface CustomTranslation {
  id: string;
  source_text: string;
  source_language: string;
  target_language: string;
  translated_text: string;
  context: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface TranslationSettings {
  id: string;
  libretranslate_api_url: string;
  libretranslate_api_key: string | null;
  auto_translate: boolean;
  show_language_selector: boolean;
  default_language: string;
  fallback_to_api: boolean;
  created_at: string;
  updated_at: string;
}

export const useTranslations = () => {
  const [translations, setTranslations] = useState<CustomTranslation[]>([]);
  const [settings, setSettings] = useState<TranslationSettings | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTranslations();
    fetchSettings();
  }, []);

  const fetchTranslations = async () => {
    try {
      const { data, error } = await supabase
        .from('custom_translations')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching translations:', error);
        return;
      }

      setTranslations(data || []);
    } catch (error) {
      console.error('Error fetching translations:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('translation_settings')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching translation settings:', error);
        return;
      }

      setSettings(data);
    } catch (error) {
      console.error('Error fetching translation settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomTranslation = (sourceText: string, sourceLanguage: string, targetLanguage: string) => {
    return translations.find(t => 
      t.source_text.toLowerCase() === sourceText.toLowerCase() &&
      t.source_language === sourceLanguage &&
      t.target_language === targetLanguage
    );
  };

  const translateText = async (text: string, fromLang: string, toLang: string): Promise<string> => {
    // First check custom translations
    const customTranslation = getCustomTranslation(text, fromLang, toLang);
    if (customTranslation) {
      return customTranslation.translated_text;
    }

    // If no custom translation and API fallback is enabled, use LibreTranslate
    if (settings?.fallback_to_api && fromLang !== toLang) {
      try {
        const { data, error } = await supabase.functions.invoke('translate-text', {
          body: {
            text,
            source: fromLang,
            target: toLang
          }
        });

        if (error) {
          console.error('Translation API error:', error);
          return text; // Return original text on error
        }

        return data.translatedText || text;
      } catch (error) {
        console.error('Translation error:', error);
        return text;
      }
    }

    return text; // Return original text if no translation available
  };

  return { 
    translations, 
    settings,
    loading, 
    refetch: fetchTranslations,
    refetchSettings: fetchSettings,
    getCustomTranslation,
    translateText
  };
};